/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Agent Chat Component
 * Real-time chat interface with WebSocket for streaming agent execution
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as BackIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { markSubAgentEventsStopped } from '../../utils/subAgentEvents';
import 'highlight.js/styles/github-dark.css';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import { formatStoredTime } from '../../utils/datetime';

import { agentApi } from '../../services/api';
import { authService } from '../../services/auth';
import { PageHeader, LoadingSpinner } from '../common';
import MarkdownRenderer from '../common/MarkdownRenderer';
import AgentSessionList from './AgentSessionList';
import useVoiceChat from '../../hooks/useVoiceChat';
import { MicButton, VoiceOverlay, VoiceSettings } from '../voice';
import AttachmentMessage from '../chat/AttachmentMessage';
import SubAgentEventsTree from '../chat/SubAgentEventsTree';
import stripMarkdown from '../../utils/stripMarkdown';
import {
  getAudioInputDisabledReason,
  isAudioInputSupported,
} from '../../utils/audioInputSupport';
import {
  attachSubAgentEvent,
  flushPendingSubAgentEvents,
  isSubAgentEvent,
} from '../../utils/subAgentEvents';
import { getExecutionProgressLabel } from '../../utils/executionProgress';
import type { WebSocketMessage, ExecutionUpdate, ConversationMessage } from '../../types';

interface ChatMessage {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL_CALL';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  toolName?: string;
  toolArgs?: Record<string, any>;
  toolResult?: any;
  toolSuccess?: boolean;
  toolCallId?: string;
  subAgentEvents?: any[];
}

const STOP_PENDING_ACTIVITY_TYPES = new Set([
  'tools_loaded',
  'iteration_started',
  'llm_thinking',
  'assistant_chunk',
  'llm_response',
  'assistant_message',
  'message_received',
  'tool_call',
  'tool_result',
  'execution_data',
]);

const STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES = new Set([
  'execution_started',
  'tool_call',
  'llm_response',
]);

function shouldIgnoreDuringStop(data: any) {
  if (!data) return false;
  if (isSubAgentEvent(data)) {
    return STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES.has(data.type);
  }
  return STOP_PENDING_ACTIVITY_TYPES.has(data.type);
}

const isCancelledToolResult = (result: any) => Boolean(
  result && (result.cancelled === true || result.data?.cancelled === true)
);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const createStoppedToolResult = () => ({
  success: false,
  data: { cancelled: true },
  error: 'Execution cancelled by user.',
});

const markPendingToolCallsStopped = (messages: ChatMessage[]): ChatMessage[] => {
  let changed = false;
  const next = messages.map((message) => {
    let updated = message;

    if (Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0) {
      const nested = markSubAgentEventsStopped(message.subAgentEvents);
      if (nested !== message.subAgentEvents) {
        updated = { ...updated, subAgentEvents: nested };
        changed = true;
      }
    }

    if (
      updated.role !== 'TOOL_CALL' ||
      (updated.toolResult !== undefined && updated.toolResult !== null)
    ) {
      return updated;
    }

    changed = true;
    return {
      ...updated,
      toolResult: createStoppedToolResult(),
      toolSuccess: false,
    };
  });

  return changed ? next : messages;
};

/* ── ToolCallCard ── collapsible per-tool card ──────────────────────────── */
const ToolCallCard: React.FC<{ message: ChatMessage; isDark: boolean }> = ({ message, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const hasResult = message.toolResult !== undefined && message.toolResult !== null;
  const isCancelled = isCancelledToolResult(message.toolResult);
  const success = message.toolSuccess === true;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5,
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
        }}
      >
        <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', flex: 1 }}>
          {message.toolName}
        </Typography>
        <Chip
          label={!hasResult ? 'Running...' : isCancelled ? 'Stopped' : success ? 'Done' : 'Error'}
          size="small"
          color={!hasResult ? 'warning' : isCancelled ? 'default' : success ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
        <ExpandMoreIcon
          sx={{
            fontSize: 16, color: 'text.secondary',
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
      {expanded && (
        <Box sx={{ borderTop: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', p: 1 }}>
          {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
            <Box sx={{ mb: hasResult ? 0.75 : 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Arguments</Typography>
              <Box sx={{
                mt: 0.25, p: 0.75, borderRadius: 0.5, fontSize: '0.75rem',
                fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                maxHeight: 120, overflow: 'auto',
              }}>
                {JSON.stringify(message.toolArgs, null, 2)}
              </Box>
            </Box>
          )}
          {hasResult && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Result</Typography>
              <Box sx={{
                mt: 0.25, p: 0.75, borderRadius: 0.5, fontSize: '0.75rem',
                fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                color: success ? 'success.main' : isCancelled ? 'text.secondary' : 'error.main',
                maxHeight: 200, overflow: 'auto',
              }}>
                {typeof message.toolResult === 'string' ? message.toolResult : JSON.stringify(message.toolResult, null, 2)}
              </Box>
            </Box>
          )}
          {Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0 && (
            <Box sx={{ mt: hasResult || (message.toolArgs && Object.keys(message.toolArgs).length > 0) ? 0.75 : 0 }}>
              <SubAgentEventsTree events={message.subAgentEvents} />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

/* ── ToolCallGroup ── groups consecutive tool calls into a collapsible container ── */
const ToolCallGroup: React.FC<{ toolMsgs: ChatMessage[]; isDark: boolean }> = ({ toolMsgs, isDark }) => {
  const [collapsed, setCollapsed] = useState(true);
  const allDone = toolMsgs.every(m => m.toolResult !== undefined && m.toolResult !== null);
  const cancelledCount = toolMsgs.filter(m => isCancelledToolResult(m.toolResult)).length;
  const allCancelled = allDone && cancelledCount === toolMsgs.length;
  const anyFailed = toolMsgs.some(m => m.toolSuccess === false && !isCancelledToolResult(m.toolResult));
  const statusLabel = !allDone ? 'Running...' : allCancelled ? 'Stopped' : (anyFailed || cancelledCount > 0) ? 'Partial' : 'Done';
  const statusColor: 'warning' | 'success' | 'error' | 'default' = !allDone ? 'warning' : allCancelled ? 'default' : anyFailed ? 'error' : cancelledCount > 0 ? 'warning' : 'success';
  const toolNames = toolMsgs.map(m => m.toolName).join(', ');

  return (
    <Box sx={{ mb: 1, ml: { xs: 4, md: 6 } }}>
      <Paper
        sx={{
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark ? 'rgba(33, 150, 243, 0.04)' : 'rgba(33, 150, 243, 0.03)',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          }}
        >
          <Typography sx={{
            fontSize: '10px', color: 'text.secondary',
            transition: 'transform 0.15s ease',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', lineHeight: 1,
          }}>▶</Typography>
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            {toolMsgs.length} tool call{toolMsgs.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {toolNames}
          </Typography>
          <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }} />
        </Box>
        {!collapsed && (
          <Box sx={{
            borderTop: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.75,
          }}>
            {toolMsgs.map(msg => <ToolCallCard key={msg.id} message={msg} isDark={isDark} />)}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/* ── groupMessages ── groups consecutive TOOL_CALL messages ─────────────── */
type GroupedItem = ChatMessage | { type: 'tool_group'; id: string; msgs: ChatMessage[] };
function groupMessages(messages: ChatMessage[]): GroupedItem[] {
  const result: GroupedItem[] = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'TOOL_CALL') {
      const group: ChatMessage[] = [];
      while (i < messages.length && messages[i].role === 'TOOL_CALL') {
        group.push(messages[i]);
        i++;
      }
      result.push({ type: 'tool_group', id: `tg-${group[0].id}`, msgs: group });
    } else {
      result.push(messages[i]);
      i++;
    }
  }
  return result;
}

export const AgentChat: React.FC<{ agentIdProp?: number; hideHeader?: boolean }> = ({ agentIdProp, hideHeader = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentId = agentIdProp ?? parseInt(id || '0');
  const agentChatAccess = useActionAccess(AI_ACTION_SURFACES.agentChatSend);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const wsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pendingSubAgentEventsRef = useRef<Record<string, any[]>>({});
  const stopPendingRef = useRef(false);
  const hasToolResultsRef = useRef(false);
  const [progressLabel, setProgressLabel] = useState('Understanding your request');

  // Fetch agent details
  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentApi.get(agentId),
    enabled: !!agentId,
  });

  const audioInputSupported = isAudioInputSupported(agent?.model);
  const audioInputDisabledReason = getAudioInputDisabledReason(agent?.model);
  const voiceButtonDisabled = agentChatAccess.disabled || !audioInputSupported;
  const voiceButtonDisabledReason = agentChatAccess.disabled
    ? agentChatAccess.blockedMessage
    : audioInputDisabledReason;

  const handleVoiceRecording = useCallback(async (file: File) => {
    if (!isConnected || isExecuting) return;
    if (!agentChatAccess.ensureAccess()) return;
    if (!audioInputSupported) {
      toast.error(audioInputDisabledReason);
      return;
    }

    try {
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const messageContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'USER',
        content: messageContent,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const wsMessage: WebSocketMessage = {
        type: 'execute',
        message: messageContent,
        session_id: currentSessionId || undefined,
        attachment_ids: [attachment.id],
        context: { attachment_ids: [attachment.id] },
        max_iterations: 50,
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(wsMessage));
        setIsExecuting(true);
      } else {
        toast.error('Not connected to agent');
      }
    } catch (error) {
      console.error('Voice recording upload failed:', error);
      toast.error('Failed to send voice recording');
      throw error;
    }
  }, [agentChatAccess, audioInputDisabledReason, audioInputSupported, currentSessionId, isConnected, isExecuting]);

  // Voice chat — record and send audio to the LLM
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    voices, selectedVoice, setSelectedVoice,
    voiceRate, setVoiceRate,
    isRecordingSupported, isTTSSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
  });

  // Speak AI response when a new assistant message appears
  const prevMessagesLenRef = useRef(0);
  useEffect(() => {
    if (isVoiceOn && messages.length > prevMessagesLenRef.current) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role === 'ASSISTANT' && lastMsg.content) {
        speakResponse(stripMarkdown(lastMsg.content));
      }
    }
    prevMessagesLenRef.current = messages.length;
  }, [messages, isVoiceOn, speakResponse]);

  // If execution ends without an assistant message (timeout/error),
  // reset voice state so UI does not stay on "Processing...".
  const prevIsExecutingRef = useRef(false);
  useEffect(() => {
    const wasExecuting = prevIsExecutingRef.current;
    prevIsExecutingRef.current = isExecuting;
    if (wasExecuting && !isExecuting && voiceState === 'processing') {
      const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
      if (!lastMsg || lastMsg.role !== 'ASSISTANT') {
        stopVoice();
      }
    }
  }, [isExecuting, voiceState, messages, stopVoice]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load session from localStorage on mount only
  useEffect(() => {
    const savedSessionId = localStorage.getItem(`agent_${agentId}_session`);
    if (savedSessionId) {
      setCurrentSessionId(savedSessionId);
      loadSessionMessages(savedSessionId);
    }
  }, [agentId]);

  // Save current session to localStorage
  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem(`agent_${agentId}_session`, currentSessionId);
    } else {
      localStorage.removeItem(`agent_${agentId}_session`);
    }
  }, [currentSessionId, agentId]);

  // Load messages from a session
  const loadSessionMessages = async (sessionId: string) => {
    try {
      console.log('Loading session:', sessionId);
      const session = await agentApi.getSession(sessionId);
      console.log('Session loaded:', session);
      console.log('Session messages:', session.messages);
      
      if (session.messages && session.messages.length > 0) {
        const loadedMessages: ChatMessage[] = session.messages.map((msg: any) => {
          if (msg.role === 'TOOL_CALL' || msg.role === 'tool_call') {
            return {
              id: msg.id?.toString() || `tool-${Date.now()}`,
              role: 'TOOL_CALL' as const,
              content: '',
              timestamp: new Date(msg.created_at),
              toolName: msg.tool_name,
              toolArgs: msg.tool_input,
              toolResult: msg.tool_output,
              toolSuccess: msg.success,
              toolCallId: msg.tool_call_id || msg.id?.toString(),
              subAgentEvents: Array.isArray(msg.sub_agent_events)
                ? msg.sub_agent_events
                : [],
            };
          }
          return {
            id: msg.id.toString(),
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          };
        });
        console.log('Loaded messages:', loadedMessages);
        pendingSubAgentEventsRef.current = {};
        setMessages(loadedMessages);
        // toast.success(`Loaded ${loadedMessages.length} messages`); // Disabled - too frequent
      } else {
        console.log('No messages in session');
        pendingSubAgentEventsRef.current = {};
        setMessages([]);
        // toast.info('This conversation has no messages yet'); // Disabled - unnecessary
      }
    } catch (error) {
      console.error('Failed to load session messages:', error);
      toast.error('Failed to load conversation history');
    }
  };

  // WebSocket connection
  useEffect(() => {
    if (!agentId) return;

    // Get auth token
    const token = authService.getAccessToken();
    if (!token) {
      toast.error('Authentication required');
      navigate('/login');
      return;
    }

    // WebSocket URL - connect to Django backend
    // In production (HTTPS), use default port (443) so nginx can proxy to Django
    // In development (HTTP), use port 8000 directly
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname;
    const isProduction = window.location.protocol === 'https:';
    const wsPort = isProduction ? '' : ':8000';
    const wsUrl = `${wsProtocol}//${wsHost}${wsPort}/ws/agents/${agentId}/chat/?token=${token}`;

    console.log('Connecting to WebSocket:', wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      // toast.success('Connected to agent'); // Disabled - too frequent
    };

    ws.onmessage = (event) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data);
        console.log('WebSocket message:', data);

        if (stopPendingRef.current && shouldIgnoreDuringStop(data)) {
          console.log('Ignoring late activity after stop request:', data.type);
          return;
        }

        if (isSubAgentEvent(data)) {
          setMessages((prev) => {
            const updated = attachSubAgentEvent(prev, data);
            if (updated === prev) {
              const key = data.parent_tool_call_id;
              const pending = pendingSubAgentEventsRef.current;
              if (!pending[key]) pending[key] = [];
              pending[key].push(data);
            }
            return updated;
          });
          return;
        }

        if (data.type === 'connected') {
          // Connection confirmed
          console.log('Connection confirmed');
        } else if (data.type === 'session_started') {
          // Backend created/loaded session
          if (data.session_id && data.session_id !== currentSessionId) {
            setCurrentSessionId(data.session_id);
          }
        } else if (data.type === 'execution_started') {
          stopPendingRef.current = false;
          setCurrentExecutionId(data.execution_id || null);
          setIsExecuting(true);
          hasToolResultsRef.current = false;
          setProgressLabel(
            getExecutionProgressLabel('execution_started') || 'Understanding your request'
          );
        } else if (data.type === 'tools_loaded') {
          // Optional: show tool count
          console.log(`Tools loaded: ${data.tool_count}`);
          setProgressLabel(
            getExecutionProgressLabel('tools_loaded', {
              hasToolResults: hasToolResultsRef.current,
            }) || 'Preparing the analysis'
          );
        } else if (data.type === 'iteration_started') {
          setProgressLabel(
            getExecutionProgressLabel('iteration_started', {
              hasToolResults: hasToolResultsRef.current,
            }) || 'Preparing the analysis'
          );
        } else if (data.type === 'llm_thinking') {
          setProgressLabel(
            getExecutionProgressLabel('llm_thinking', {
              hasToolResults: hasToolResultsRef.current,
            }) || 'Preparing the analysis'
          );
        } else if (data.type === 'execution_completed') {
          if (stopPendingRef.current && (data as any).status !== 'CANCELLED') {
            stopPendingRef.current = false;
            setMessages((prev) => markPendingToolCallsStopped(prev));
            setIsExecuting(false);
            setCurrentExecutionId(null);
            hasToolResultsRef.current = false;
            toast.info('Execution stopped');
            return;
          }
          stopPendingRef.current = false;
          if ((data as any).status === 'CANCELLED') {
            setMessages((prev) => markPendingToolCallsStopped(prev));
            setIsExecuting(false);
            setCurrentExecutionId(null);
            hasToolResultsRef.current = false;
            toast.info((data as any).message || 'Execution stopped');
            return;
          }
          // Add assistant response message
          if (data.result && data.result.response) {
            const assistantMessage: ChatMessage = {
              id: `assistant-${Date.now()}`,
              role: 'ASSISTANT',
              content: data.result.response,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
          }
          setIsExecuting(false);
          setCurrentExecutionId(null);
          hasToolResultsRef.current = false;
          toast.success('Execution completed');
          
          // Auto-generate title for new sessions after first message
          if (currentSessionId && messages.length === 1) {
            // Fire and forget - don't await to avoid blocking
            agentApi.generateSessionTitle(currentSessionId)
              .then(() => console.log('Session title generated'))
              .catch((error) => console.error('Failed to generate title:', error));
          }
        } else if (data.type === 'execution_error') {
          stopPendingRef.current = false;
          toast.error(data.error || 'Execution error');
          setIsExecuting(false);
          setCurrentExecutionId(null);
          hasToolResultsRef.current = false;
        } else if (data.type === 'execution_stopped') {
          stopPendingRef.current = false;
          setMessages((prev) => markPendingToolCallsStopped(prev));
          setIsExecuting(false);
          setCurrentExecutionId(null);
          hasToolResultsRef.current = false;
          toast.info(data.message || 'Execution stopped');
        } else if (data.type === 'stop_requested') {
          stopPendingRef.current = true;
          toast.info(data.message || 'Stop request sent');
          // Keep isExecuting true until we get completion or error
        } else if (data.type === 'tool_call') {
          setProgressLabel(
            getExecutionProgressLabel('tool_call', {
              hasToolResults: hasToolResultsRef.current,
            }) || 'Gathering the required evidence'
          );
          // Tool being called — add a TOOL_CALL message
          setMessages((prev) => {
            const nextMessage: ChatMessage = {
              id: `tool-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: 'TOOL_CALL',
              content: '',
              timestamp: new Date(),
              toolName: data.tool_name,
              toolArgs: data.tool_args || data.arguments,
              toolCallId: (data as any).tool_call_id,
              subAgentEvents: [],
            };
            return flushPendingSubAgentEvents(
              [...prev, nextMessage],
              pendingSubAgentEventsRef.current,
            );
          });
        } else if (data.type === 'tool_result') {
          hasToolResultsRef.current = true;
          setProgressLabel(
            getExecutionProgressLabel('tool_result', {
              hasToolResults: hasToolResultsRef.current,
            }) || 'Gathering the required evidence'
          );
          // Tool result — update the last matching TOOL_CALL message
          setMessages((prev) => {
            const updated = [...prev];
            const resultToolCallId = (data as any).tool_call_id;
            for (let i = updated.length - 1; i >= 0; i--) {
              const isMatchingTool =
                updated[i].role === 'TOOL_CALL' &&
                !updated[i].toolResult &&
                (!resultToolCallId || updated[i].toolCallId === resultToolCallId);
              if (isMatchingTool) {
                updated[i] = {
                  ...updated[i],
                  toolResult: data.result,
                  toolSuccess: data.result?.success !== false,
                };
                break;
              }
            }
            return updated;
          });
        } else if (data.type === 'execution_update') {
          handleExecutionUpdate(data.data as ExecutionUpdate);
        } else if (data.type === 'error') {
          toast.error(data.error || 'Execution error');
          setIsExecuting(false);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
      setIsConnected(false);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
      // toast.info('Disconnected from agent'); // Disabled - too frequent
    };

    // Cleanup on unmount
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [agentId, navigate]);

  // Handle execution updates from WebSocket
  const handleExecutionUpdate = (update: ExecutionUpdate) => {
    console.log('Execution update:', update);

    if (update.type === 'message' && update.message) {
      // Add assistant message
      const newMessage: ChatMessage = {
        id: update.message.id.toString(),
        role: update.message.role,
        content: update.message.content,
        timestamp: new Date(update.message.created_at),
      };
      setMessages((prev) => [...prev, newMessage]);
    } else if (update.type === 'step' && update.step) {
      // Show step information
      if (update.step.step_type === 'REASONING') {
        const stepMessage: ChatMessage = {
          id: `step-${update.step.id}`,
          role: 'SYSTEM',
          content: `💭 ${update.step.content}`,
          timestamp: new Date(update.step.created_at),
        };
        setMessages((prev) => [...prev, stepMessage]);
      } else if (update.step.step_type === 'TOOL_CALL') {
        const toolMessage: ChatMessage = {
          id: `step-${update.step.id}`,
          role: 'SYSTEM',
          content: `🔧 Using tool: ${update.step.tool_name}`,
          timestamp: new Date(update.step.created_at),
        };
        setMessages((prev) => [...prev, toolMessage]);
      }
    } else if (update.type === 'complete') {
      setIsExecuting(false);
      setCurrentExecutionId(null);
      toast.success('Execution completed');
    } else if (update.type === 'error') {
      setIsExecuting(false);
      setCurrentExecutionId(null);
      toast.error(update.error || 'Execution failed');
    }
  };

  // Send message
  const handleSendMessage = () => {
    if (!inputMessage.trim() || !isConnected || isExecuting) return;
    if (!agentChatAccess.ensureAccess()) return;

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'USER',
      content: inputMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Send to WebSocket with session_id
    const wsMessage: WebSocketMessage = {
      type: 'execute',
      message: inputMessage,
      session_id: currentSessionId || undefined,
      max_iterations: 50,
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(wsMessage));
      hasToolResultsRef.current = false;
      setProgressLabel('Understanding your request');
      setIsExecuting(true);
      setInputMessage('');
      
      // Auto-generate title after first message if new session
      if (!currentSessionId) {
        // Will be set by session_started event from backend
      }
    } else {
      toast.error('Not connected to agent');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Stop execution
  const handleStopExecution = () => {
    if (!currentExecutionId || !wsRef.current) return;

    const wsMessage: WebSocketMessage = {
      type: 'stop',
      execution_id: currentExecutionId,
    };

    if (wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(wsMessage));
      stopPendingRef.current = true;
      toast.info('Stopping execution...');
    } else {
      toast.error('Not connected to agent');
    }
  };

  const handleBack = () => {
    navigate(`/ai/agents/${agentId}`);
  };

  const handleClearChat = () => {
    setMessages([]);
    toast.info('Chat cleared');
  };

  // Handle session selection
  const handleSessionSelect = (sessionId: string | null) => {
    setCurrentSessionId(sessionId);
    if (sessionId) {
      loadSessionMessages(sessionId);
    } else {
      setMessages([]);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth < 900) {
      setSidebarOpen(false);
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    // Check if current session is already empty (no messages) - don't create another
    if (messages.length === 0 && currentSessionId === null) {
      console.log('handleNewConversation - already in a fresh state, skipping');
      return;
    }
    
    console.log('handleNewConversation called - clearing session');
    // Clear localStorage immediately to prevent reload issues
    localStorage.removeItem(`agent_${agentId}_session`);
    console.log('localStorage cleared, currentSessionId was:', currentSessionId);
    
    // Clear state
    setCurrentSessionId(null);
    setMessages([]);
    console.log('State cleared - currentSessionId set to null, messages cleared');
    
    toast.info('Started new conversation');
    // Close sidebar on mobile
    if (window.innerWidth < 900) {
      setSidebarOpen(false);
    }
  };

  if (isLoading || !agent) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={hideHeader ? {
      // Embedded: fill parent container only
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      overflow: 'hidden',
      bgcolor: 'background.default',
      minHeight: 0,
    } : {
      // Route page: stay in normal layout flow so the shared app top bar remains visible.
      position: 'relative',
      width: '100%',
      height: 'calc(100vh - var(--topbar-height, 0px) - 16px)',
      display: 'flex',
      overflow: 'hidden',
      bgcolor: 'background.default',
      minHeight: 0,
    }}>
      {/* Session Sidebar - Drawer on mobile, inline on desktop. Hidden in embedded mode. */}
      {hideHeader ? null : isMobile ? (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: { width: '85vw', maxWidth: 360 },
          }}
        >
          <AgentSessionList
            agentId={agentId}
            currentSessionId={currentSessionId}
            onSessionSelect={(sessionId) => { handleSessionSelect(sessionId); setSidebarOpen(false); }}
            onNewConversation={() => { handleNewConversation(); setSidebarOpen(false); }}
          />
        </Drawer>
      ) : (
        sidebarOpen && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
              overflow: 'hidden',
              transition: 'width 0.3s ease-in-out',
            }}
          >
            <AgentSessionList
              agentId={agentId}
              currentSessionId={currentSessionId}
              onSessionSelect={handleSessionSelect}
              onNewConversation={handleNewConversation}
            />
          </Box>
        )
      )}

      {/* Main Chat Area */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'all 0.3s ease-in-out',
          minHeight: 0,
        }}
      >
        {!hideHeader && (
          <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', px: { xs: 1.5, md: 3 }, pt: { xs: 1, md: 2 }, pb: { xs: 1.5, md: 3 } }}>
            <PageHeader
              title={`Chat with ${agent.name}`}
              breadcrumbs={[
                { label: 'Agents', to: '/ai/agents' },
                { label: agent.name, to: `/ai/agents/${agentId}` },
                { label: 'Chat' },
              ]}
              actions={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} size="small">
                      <MenuIcon />
                    </IconButton>
                  <Chip
                    label={isConnected ? 'Connected' : 'Disconnected'}
                    color={isConnected ? 'success' : 'error'}
                    size="small"
                  />
                    <IconButton onClick={handleClearChat} size="small">
                      <RefreshIcon />
                    </IconButton>
                    <IconButton onClick={() => navigate(`/ai/agents/${agentId}`)} size="small">
                      <SettingsIcon />
                    </IconButton>
                </Box>
              }
            />
          </Box>
        )}

        {/* Chat Messages Area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: { xs: 1.5, md: 3 },
            bgcolor: 'background.default',
            backgroundImage: isDark
              ? 'linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px)',
            backgroundSize: '100% 20px',
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <BotIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
              <Typography variant="h5" gutterBottom fontWeight={500}>
                Start a conversation
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {currentSessionId ? 'Continue your conversation' : 'Ask the agent anything to get started'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
              {groupMessages(messages).map((item) => {
                if ('type' in item && item.type === 'tool_group') {
                  return <ToolCallGroup key={item.id} toolMsgs={item.msgs} isDark={isDark} />;
                }
                const message = item as ChatMessage;
                return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    mb: 3,
                    flexDirection: message.role === 'USER' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        message.role === 'USER'
                          ? 'primary.main'
                          : message.role === 'SYSTEM'
                          ? 'secondary.main'
                          : 'success.main',
                      mx: { xs: 0.5, md: 1.5 },
                      width: { xs: 28, md: 40 },
                      height: { xs: 28, md: 40 },
                      boxShadow: 1,
                    }}
                  >
                    {message.role === 'USER' ? (
                      <PersonIcon />
                    ) : message.role === 'SYSTEM' ? (
                      <SettingsIcon fontSize="small" />
                    ) : (
                      <BotIcon />
                    )}
                  </Avatar>

                  <Box sx={{ maxWidth: { xs: '100%', sm: '75%' }, minWidth: 200 }}>
                    <Paper
                      elevation={message.role === 'SYSTEM' ? 0 : 2}
                      sx={{
                        p: 2.5,
                        bgcolor:
                          message.role === 'USER'
                            ? 'primary.main'
                            : message.role === 'SYSTEM'
                            ? isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.06)'
                            : 'background.paper',
                        color: message.role === 'USER' ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 2,
                        border: message.role === 'SYSTEM' ? '1px solid' : 'none',
                        borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
                        boxShadow: message.role === 'SYSTEM' ? 'none' : undefined,
                      }}
                    >
                      {message.role !== 'USER' && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 1,
                            fontWeight: 600,
                            color: message.role === 'SYSTEM' ? 'info.main' : 'success.main',
                          }}
                        >
                          {message.role === 'SYSTEM' ? '⚙️ System' : '🤖 Assistant'}
                        </Typography>
                      )}
                      <Box 
                        sx={{ 
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          '& p': { margin: '0.5em 0' },
                          '& p:first-of-type': { marginTop: 0 },
                          '& p:last-of-type': { marginBottom: 0 },
                          '& pre': { 
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            padding: '12px',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.875rem',
                          },
                          '& code': {
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '0.875em',
                          },
                          '& pre code': {
                            backgroundColor: 'transparent',
                            padding: 0,
                          },
                          '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
                          '& li': { margin: '0.25em 0' },
                          '& h1, & h2, & h3, & h4, & h5, & h6': { 
                            margin: '1em 0 0.5em 0',
                            fontWeight: 600,
                          },
                          '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: message.role === 'USER'
                              ? 'rgba(255,255,255,0.3)'
                              : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            paddingLeft: '1em',
                            margin: '0.5em 0',
                            fontStyle: 'italic',
                          },
                          '& a': {
                            color: message.role === 'USER' ? 'rgba(255,255,255,0.9)' : 'primary.main',
                            textDecoration: 'underline',
                          },
                          '& table': {
                            borderCollapse: 'collapse',
                            width: '100%',
                            margin: '0.5em 0',
                          },
                          '& th, & td': {
                            border: '1px solid',
                            borderColor: message.role === 'USER'
                              ? 'rgba(255,255,255,0.3)'
                              : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                            padding: '8px',
                            textAlign: 'left',
                          },
                          '& th': {
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {message.role === 'USER' ? (
                          <AttachmentMessage content={message.content} />
                        ) : (
                          <MarkdownRenderer content={message.content} isDark={isDark} />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          opacity: message.role === 'USER' ? 0.8 : 0.6,
                          fontSize: '0.75rem',
                        }}
                      >
                        {formatStoredTime(message.timestamp, {
                          hour: '2-digit',
                          minute: '2-digit',
                        }, '')}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
                );
              })}

              {isExecuting && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 3,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'success.main', mx: 1.5, width: 40, height: 40, boxShadow: 1 }}>
                    <BotIcon />
                  </Avatar>
                  <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={5} />
                      <Typography variant="body2" color="text.secondary">
                        {progressLabel}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Paper sx={{ p: { xs: 1.5, md: 2.5 }, boxShadow: 3, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          {!isConnected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Not connected to agent. Reconnecting...
            </Alert>
          )}

          {/* Voice overlay */}
          {isVoiceOn && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <VoiceOverlay voiceState={voiceState} interimTranscript={interimTranscript} />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, maxWidth: 1000, mx: 'auto', alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={isMobile ? 4 : 6}
              placeholder={agentChatAccess.disabled ? agentChatAccess.blockedMessage : isVoiceOn ? "Recording voice message..." : (currentSessionId ? "Continue the conversation..." : "Type your message to start...")}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!isConnected || isExecuting || agentChatAccess.disabled}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            
            {/* Mic button */}
            <MicButton
              voiceState={voiceState}
              isRecordingSupported={isRecordingSupported}
              disabled={voiceButtonDisabled}
              disabledReason={voiceButtonDisabledReason}
              onClick={toggleVoice}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flexShrink: 0 }}
            />

            {/* Voice settings */}
            {isTTSSupported && (
              <VoiceSettings
                voices={voices}
                selectedVoice={selectedVoice}
                onVoiceChange={setSelectedVoice}
                voiceRate={voiceRate}
                onRateChange={setVoiceRate}
                disabled={isVoiceOn}
              />
            )}

            {isExecuting ? (
              <Button
                variant="outlined"
                color="error"
                endIcon={<StopIcon />}
                onClick={handleStopExecution}
                disabled={!currentExecutionId}
                sx={{ 
                  minWidth: { xs: 'auto', md: 120 },
                  height: { xs: 40, md: 56 },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  fontWeight: 600,
                  borderWidth: 2,
                  px: { xs: 1.5, md: 2 },
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)',
                  },
                }}
              >
                Stop
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || !isConnected || agentChatAccess.disabled}
                sx={{ 
                  minWidth: { xs: 'auto', md: 120 },
                  height: { xs: 40, md: 56 },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, md: 2 },
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
                title={agentChatAccess.disabled ? agentChatAccess.blockedMessage : undefined}
              >
                Send
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentChat;
